import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Collapse } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import classNames from 'classnames';
import { AlyceTheme, LinkButton } from '@alycecom/ui';

const useStyles = makeStyles<AlyceTheme, { lineHeight: number; lines: number }>(({ palette, spacing }) => ({
  button: {
    fontWeight: 'normal',
  },
  truncated: ({ lineHeight, lines }) => ({
    overflow: 'hidden',
    position: 'relative',
    maxHeight: lineHeight * (lines + 1),
    marginBottom: spacing(2),
    '&:after': {
      content: "''",
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: '100%',
      height: lineHeight * 3,
      background: `linear-gradient(to bottom, ${alpha(palette.common.white, 0)}, ${alpha(
        palette.common.white,
        1,
      )} 80%)`,
    },
  }),
}));

export interface ITruncatedTextProps {
  children: React.ReactNode;
  lines?: number;
  lineHeight?: number;
  overrideClasses?: Record<string, string>;
}

const TruncatedText = ({ children, lines = 3, lineHeight = 24, overrideClasses = {} }: ITruncatedTextProps) => {
  const truncateHeight = lineHeight * (lines + 1);
  const classes = useStyles({ lineHeight, lines });

  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncatedOptions, setIsTruncatedOptions] = useState(false);
  const [useTruncated, setUseTruncated] = useState(false);

  const toggleTruncate = useCallback(() => setIsTruncatedOptions(!isTruncatedOptions), [isTruncatedOptions]);

  useEffect(() => {
    if (textRef.current) {
      if (textRef.current.offsetHeight > truncateHeight) {
        setIsTruncatedOptions(true);
        setUseTruncated(true);
      }
    }
  }, [textRef, truncateHeight]);

  useEffect(() => {
    const { current } = textRef;
    if (isTruncatedOptions && current) {
      current.scrollTop = 0;
    }
  }, [textRef, isTruncatedOptions]);

  return (
    <>
      <Collapse in={!isTruncatedOptions} collapsedSize={truncateHeight}>
        <Box
          ref={textRef}
          className={classNames({
            [classes.truncated]: isTruncatedOptions,
            [overrideClasses.truncated]: isTruncatedOptions,
            [overrideClasses.nonTruncated]: !isTruncatedOptions,
          })}
        >
          {children}
        </Box>
      </Collapse>
      {useTruncated && (
        <Box mt={1} mb={2} className={overrideClasses.buttonWrapper}>
          <LinkButton className={classNames(classes.button, overrideClasses.button)} onClick={toggleTruncate}>
            Show {isTruncatedOptions ? 'more' : 'less'}
          </LinkButton>
        </Box>
      )}
    </>
  );
};

export default memo(TruncatedText);
